type PlainRecord = Record<string, unknown>;
type Container = PlainRecord | unknown[];

const PROTO_KEY = '__proto__';
const CONSTRUCTOR_KEY = 'constructor';
const PROTOTYPE_KEY = 'prototype';

/** Parsed JSON only ever yields plain objects and arrays; class instances are never walked. */
const isPlainObject = (value: unknown): value is PlainRecord => {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

const isContainer = (value: unknown): value is Container => Array.isArray(value) || isPlainObject(value);

// Consumers compile this source under their own `lib` (reporting-performance-test targets es2019), so `Object.hasOwn`
// (es2022) is not available here.
const hasOwnKey = (record: PlainRecord, key: string): boolean =>
  Object.getOwnPropertyDescriptor(record, key) !== undefined;

/**
 * An own `__proto__` key reparents whatever it is later merged or assigned into, so it is always dangerous.
 * `constructor` and `prototype` are ordinary data keys on their own — a row or column value can be named either,
 * and saved `expandedRows` state holds `{ "constructor": {} }` for such a row — so `constructor` is only dangerous
 * when it carries a `prototype` object, which is the `constructor.prototype` path a naive deep merge follows.
 */
const isDangerousEntry = (key: string, value: unknown): boolean => {
  if (key === PROTO_KEY) {
    return true;
  }
  return key === CONSTRUCTOR_KEY && isPlainObject(value) && hasOwnKey(value, PROTOTYPE_KEY);
};

const hasDangerousEntry = (container: Container): boolean =>
  Object.entries(container).some(([key, value]) => isDangerousEntry(key, value));

/**
 * Whether parsed JSON contains a key that could pollute `Object.prototype` once merged into another object.
 * Walks iteratively so deeply nested input cannot overflow the stack, and tolerates cycles.
 */
export function hasDangerousKeys(value: unknown): boolean {
  const pending: unknown[] = [value];
  const visited = new Set<Container>();

  while (pending.length > 0) {
    const current = pending.pop();
    if (!isContainer(current) || visited.has(current)) {
      continue;
    }
    visited.add(current);
    if (hasDangerousEntry(current)) {
      return true;
    }
    for (const child of Object.values(current)) {
      pending.push(child);
    }
  }

  return false;
}

const createEmptyCopy = (container: Container): Container => (Array.isArray(container) ? [] : {});

/** Returns the copy standing in for `child`, queueing any container not seen yet so its entries get filled in. */
const copyFor = (child: unknown, copies: Map<Container, Container>, pending: Container[]): unknown => {
  if (!isContainer(child)) {
    return child;
  }
  const existing = copies.get(child);
  if (existing) {
    return existing;
  }
  const copy = createEmptyCopy(child);
  copies.set(child, copy);
  pending.push(child);
  return copy;
};

/** Iterative, like `hasDangerousKeys`, so input deep enough to reach this point cannot overflow the stack here either. */
const copyWithoutDangerousKeys = (root: Container): Container => {
  const copies = new Map<Container, Container>();
  const pending: Container[] = [];
  const rootCopy = copyFor(root, copies, pending) as Container;

  let source = pending.pop();
  while (source) {
    const target = copies.get(source) as PlainRecord;
    for (const [key, child] of Object.entries(source)) {
      if (!isDangerousEntry(key, child)) {
        target[key] = copyFor(child, copies, pending);
      }
    }
    source = pending.pop();
  }

  return rootCopy;
};

/**
 * Returns `value` with every dangerous key (see `hasDangerousKeys`) removed. The input is never mutated: when there is
 * nothing to remove the same reference comes back, otherwise a copy of the plain-data parts is returned.
 */
export function stripDangerousKeys<T>(value: T): T {
  if (!isContainer(value) || !hasDangerousKeys(value)) {
    return value;
  }
  return copyWithoutDangerousKeys(value) as T;
}
